import { apiClient } from '@lib/api/client';
import type {
  CompaniesResponse,
  CompaniesSearchResponse,
  SelectCompanyResponse,
  SelectedCompanyResponse,
} from '../types/company.types';
import type {
  CreateCompanyPayload,
  CreateCompanyResponse,
} from '../types/newCompanyWizard.types';
// SUBSCRIPTIONS_DISABLED: import type { SubscriptionVerificationResponse } from '@features/subscriptions/types/subscription.types';

/**
 * Company Service
 * Handles company-related API calls through the BFF
 */
export const companyService = {
  /**
   * Get user's companies (cached)
   */
  async getCompanies(): Promise<CompaniesResponse> {
    const response = await apiClient.get<CompaniesResponse>('/companies');
    return response.data;
  },

  /**
   * Search companies with pagination
   */
  async searchCompanies(
    page: number = 1,
    pageSize: number = 12,
    search?: string
  ): Promise<CompaniesSearchResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    const response = await apiClient.get<CompaniesSearchResponse>(`/companies/search?${params}`);
    return response.data;
  },

  /**
   * Create a new company. La compañía creada queda automáticamente
   * seleccionada en la sesión (ver company.controller.ts en el BFF).
   */
  async createCompany(payload: CreateCompanyPayload): Promise<CreateCompanyResponse> {
    const response = await apiClient.post<CreateCompanyResponse>('/companies', payload);
    return response.data;
  },

  /**
   * Select a company for the current session
   */
  async selectCompany(companyId: string): Promise<SelectCompanyResponse> {
    const response = await apiClient.post<SelectCompanyResponse>('/companies/select', {
      companyId,
    });
    // Guardar en localStorage para validar acceso a /select-company
    localStorage.setItem('selectedCompanyId', companyId);
    return response.data;
  },

  /**
   * Get currently selected company
   */
  async getSelectedCompany(): Promise<SelectedCompanyResponse> {
    const response = await apiClient.get<SelectedCompanyResponse>('/companies/selected');
    return response.data;
  },

  /**
   * Get branch offices for the selected company
   */
  async getBranchOffices(): Promise<any[]> {
    const response = await apiClient.get<any[]>('/companies/branch-offices');
    return response.data;
  },

  /**
   * Check if the selected company has valid client certificates
   */
  async checkClientCertificates(): Promise<{ hasCertificates: boolean; certificates: any[] }> {
    const response = await apiClient.get<{ hasCertificates: boolean; certificates: any[] }>(
      '/companies/client-certificates'
    );
    return response.data;
  },

  /* SUBSCRIPTIONS_DISABLED
  async verifySubscription(): Promise<SubscriptionVerificationResponse> {
    const response = await apiClient.post<SubscriptionVerificationResponse>('/companies/verify-subscription');
    return response.data;
  },

  async createFreeSubscription(): Promise<{ subscription: any; message: string }> {
    const response = await apiClient.post<{ subscription: any; message: string }>('/companies/create-free-subscription');
    return response.data;
  },

  async getCompaniesSubscriptionStatuses(
    companies: { id: string; code: string }[]
  ): Promise<Record<string, {
    planName: string | null;
    planId?: string;
    status: string;
    isActive: boolean;
    amount?: number;
    currency?: string;
    interval?: string;
    currentPeriodEnd?: string;
  }>> {
    const response = await apiClient.post('/companies/subscription-statuses', { companies });
    return response.data;
  },
  */

};
